import { Project, TargetRole, IProject } from '../domain/Project';
import { Skill, ISkill } from '../domain/Skill';
import { Experience, IExperience } from '../domain/Experience';

/**
 * OOP Singleton Portfolio Service
 * Manages repository data, role state, and filtering operations.
 */
export class PortfolioService {
  private static instance: PortfolioService;

  private projects: Project[] = [];
  private skills: Skill[] = [];
  private experiences: Experience[] = [];

  private constructor() {
    this.initData();
  }

  public static getInstance(): PortfolioService {
    if (!PortfolioService.instance) {
      PortfolioService.instance = new PortfolioService();
    }
    return PortfolioService.instance;
  }

  private initData(): void {
    // Initialize Actual Production Projects Data (6 Handled Systems)
    const rawProjects: IProject[] = [
      {
        id: 'proj-1',
        title: 'Daily Sales Report System',
        shortDescription: 'Automated daily sales tracking and financial reporting system with real-time revenue analytics and Supabase data synchronization.',
        fullDescription: 'The Daily Sales Report System is a production-grade web application built to streamline sales logging, daily revenue aggregation, and shift reconciliation. Built with React (Vite), TypeScript, JavaScript, CSS, and Supabase backend services, it features instant calculation of gross/net sales, shift totals, and Cloudflare edge hosting.',
        category: 'full-stack',
        targetRoles: ['web-developer', 'all'],
        techStack: ['TypeScript', 'React (Vite)', 'JavaScript', 'CSS', 'Cloudflare', 'Supabase'],
        imageUrl: '/assets/project_ecommerce.jpg',
        liveDemoUrl: 'https://web-portfolio-16j.pages.dev',
        githubUrl: 'https://github.com/Zenerol',
        features: [
          'Automated Shift & Daily Revenue Aggregation',
          'Real-Time Supabase Database Synchronization',
          'Exportable Sales Summaries & Shift Audit Logs',
          'Responsive Glassmorphic UI with Cloudflare Edge Hosting'
        ],
        keyHighlights: ['Handled live business sales reporting', 'Real-time financial logging accuracy', 'Type-safe React & Supabase data flow']
      },
      {
        id: 'proj-2',
        title: 'Credit Sales System',
        shortDescription: 'Comprehensive credit sales & customer ledger management system for monitoring credit balances, payment schedules, and accounts receivable.',
        fullDescription: 'The Credit Sales System manages customer credit lines, transaction ledgers, payment installments, and outstanding balances. Developed using TypeScript, React (Vite), JavaScript, CSS, Cloudflare, and Supabase, it eliminates manual credit recordkeeping and automates account balance tracking.',
        category: 'full-stack',
        targetRoles: ['web-developer', 'all'],
        techStack: ['TypeScript', 'React (Vite)', 'JavaScript', 'CSS', 'Cloudflare', 'Supabase'],
        imageUrl: '/assets/project_network.jpg',
        liveDemoUrl: 'https://web-portfolio-16j.pages.dev',
        githubUrl: 'https://github.com/Zenerol',
        features: [
          'Customer Credit Account Ledger Management',
          'Automated Outstanding Balance & Payment Tracking',
          'Transaction History & Receipt Audit Logs',
          'Cloudflare Deployed with Supabase Row Level Security'
        ],
        keyHighlights: ['Eliminated manual ledger errors', 'Streamlined credit recovery workflow', 'Fast responsive UI with Vite & React']
      },
      {
        id: 'proj-3',
        title: 'Sales Reporting System',
        shortDescription: 'Centralized sales reporting & analytics platform generating interactive charts, revenue trends, and product performance breakdowns.',
        fullDescription: 'The Sales Reporting System provides business management with executive dashboards, sales analytics, product velocity trends, and periodic financial reporting. Built with React (Vite), TypeScript, JavaScript, CSS, Cloudflare, and Supabase database services.',
        category: 'full-stack',
        targetRoles: ['web-developer', 'all'],
        techStack: ['TypeScript', 'React (Vite)', 'JavaScript', 'CSS', 'Cloudflare', 'Supabase'],
        imageUrl: '/assets/project_task.jpg',
        liveDemoUrl: 'https://web-portfolio-16j.pages.dev',
        githubUrl: 'https://github.com/Zenerol',
        features: [
          'Interactive Sales Trend Charts & Metrics',
          'Product Performance & Fast-Mover Analytics',
          'Periodic Revenue & Profit Margin Reports',
          'Real-Time Cloud Database Queries'
        ],
        keyHighlights: ['Data-driven business analytics dashboard', 'Fast reactive UI built with Vite & React', 'Secure Cloudflare edge deployment']
      },
      {
        id: 'proj-4',
        title: 'Hog Feeds Production System',
        shortDescription: 'End-to-end feed production & inventory control system tracking raw material formulation, production batching, and stock distribution.',
        fullDescription: 'The Hog Feeds Production System is an industrial inventory and production management application designed for feed mill operations. It automates raw material batching, formula ratio logging, finished goods inventory, and dispatch logs using React (Vite), TypeScript, JavaScript, CSS, Cloudflare, and Supabase.',
        category: 'full-stack',
        targetRoles: ['web-developer', 'all'],
        techStack: ['TypeScript', 'React (Vite)', 'JavaScript', 'CSS', 'Cloudflare', 'Supabase'],
        imageUrl: '/assets/project_cloud.jpg',
        liveDemoUrl: 'https://web-portfolio-16j.pages.dev',
        githubUrl: 'https://github.com/Zenerol',
        features: [
          'Raw Material & Feed Formula Ratio Management',
          'Production Batch Logging & Yield Tracking',
          'Warehouse Inventory & Stock Dispatch Logs',
          'Cloudflare Edge & Supabase Integration'
        ],
        keyHighlights: ['Streamlined feed mill production operations', 'Accurate raw material stock inventory control', 'High-reliability production tracking']
      },
      {
        id: 'proj-5',
        title: 'Purchase Requisition Approval System',
        shortDescription: 'Automated purchase requisition & multi-tier managerial approval workflow system featuring real-time status tracking and budget verification.',
        fullDescription: 'The Purchase Requisition Approval System digitalizes corporate procurement workflows. Employees submit item requisitions, track multi-level management approvals, verify budget allocations, and generate purchase orders built with React (Vite), TypeScript, JavaScript, CSS, Cloudflare, and Supabase.',
        category: 'full-stack',
        targetRoles: ['web-developer', 'all'],
        techStack: ['TypeScript', 'React (Vite)', 'JavaScript', 'CSS', 'Cloudflare', 'Supabase'],
        imageUrl: '/assets/project_ecommerce.jpg',
        liveDemoUrl: 'https://web-portfolio-16j.pages.dev',
        githubUrl: 'https://github.com/Zenerol',
        features: [
          'Multi-Level Managerial Approval Workflows',
          'Budget Allocation & Request Verification',
          'Purchase Order Generation & Audit Trails',
          'Real-Time Notification & Status Badges'
        ],
        keyHighlights: ['Accelerated procurement approvals', 'Eliminated paper request bottlenecks', 'Type-safe Supabase & Cloudflare architecture']
      },
      {
        id: 'proj-6',
        title: 'Task Tracker System',
        shortDescription: 'Collaborative task tracking & project milestone management application with priority queuing, status Kanban columns, and Supabase cloud sync.',
        fullDescription: 'The Task Tracker System enables team members to organize daily deliverables, manage project backlogs, assign priority tags, and monitor task completion status. Built with React (Vite), TypeScript, JavaScript, CSS, Cloudflare, and Supabase database services.',
        category: 'full-stack',
        targetRoles: ['web-developer', 'all'],
        techStack: ['TypeScript', 'React (Vite)', 'JavaScript', 'CSS', 'Cloudflare', 'Supabase'],
        imageUrl: '/assets/project_task.jpg',
        liveDemoUrl: 'https://web-portfolio-16j.pages.dev',
        githubUrl: 'https://github.com/Zenerol',
        features: [
          'Interactive Task Columns & Priority Tags',
          'Team Assignment & Due Date Reminders',
          'Milestone Progress Tracker',
          'Instant Supabase Cloud Synchronization'
        ],
        keyHighlights: ['Streamlined team productivity', 'Responsive modern UI design', 'High-performance Vite & React build']
      }
    ];

    this.projects = rawProjects.map(p => new Project(p));

    // Initialize Refactored Clean Skills Data
    const rawSkills: ISkill[] = [
      // Web Development & Programming Languages
      { id: 's1', name: 'HTML5 & CSS3', category: 'web-development', proficiencyPercentage: 95, iconName: 'Code', isPrimaryWebDev: true, isPrimaryIT: true },
      { id: 's2', name: 'JavaScript (ES6+)', category: 'web-development', proficiencyPercentage: 90, iconName: 'Zap', isPrimaryWebDev: true, isPrimaryIT: true },
      { id: 's3', name: 'TypeScript', category: 'web-development', proficiencyPercentage: 88, iconName: 'FileCode', isPrimaryWebDev: true, isPrimaryIT: true },
      { id: 's4', name: 'PHP & Laravel', category: 'web-development', proficiencyPercentage: 86, iconName: 'Code', isPrimaryWebDev: true, isPrimaryIT: false },
      { id: 's5', name: 'Java', category: 'web-development', proficiencyPercentage: 82, iconName: 'Code', isPrimaryWebDev: true, isPrimaryIT: true },
      { id: 's6', name: 'React.js (Vite)', category: 'web-development', proficiencyPercentage: 88, iconName: 'Layers', isPrimaryWebDev: true, isPrimaryIT: false },
      { id: 's7', name: 'RESTful APIs', category: 'web-development', proficiencyPercentage: 88, iconName: 'Globe', isPrimaryWebDev: true, isPrimaryIT: true },

      // IT Infrastructure, Systems & Multimedia
      { id: 's8', name: 'Basic Computer Networking & Network Troubleshooting', category: 'it-infrastructure', proficiencyPercentage: 88, iconName: 'Network', isPrimaryWebDev: false, isPrimaryIT: true },
      { id: 's9', name: 'Hardware & Software Troubleshooting & Basic Repairing', category: 'it-infrastructure', proficiencyPercentage: 90, iconName: 'Wrench', isPrimaryWebDev: false, isPrimaryIT: true },
      { id: 's10', name: 'System Administration (Windows / Linux CLI)', category: 'it-infrastructure', proficiencyPercentage: 84, iconName: 'Terminal', isPrimaryWebDev: false, isPrimaryIT: true },
      { id: 's11', name: 'Graphic Design (Canva)', category: 'it-infrastructure', proficiencyPercentage: 88, iconName: 'Palette', isPrimaryWebDev: true, isPrimaryIT: true },
      { id: 's12', name: 'Video Editing (CapCut)', category: 'it-infrastructure', proficiencyPercentage: 85, iconName: 'Video', isPrimaryWebDev: true, isPrimaryIT: true },

      // Databases & Tools
      { id: 's13', name: 'MySQL & SQL Databases', category: 'databases-tools', proficiencyPercentage: 88, iconName: 'Database', isPrimaryWebDev: true, isPrimaryIT: true },
      { id: 's14', name: 'Supabase & Cloudflare Cloud Services', category: 'databases-tools', proficiencyPercentage: 88, iconName: 'Cloud', isPrimaryWebDev: true, isPrimaryIT: true },
      { id: 's15', name: 'Git & GitHub Version Control', category: 'databases-tools', proficiencyPercentage: 90, iconName: 'GitBranch', isPrimaryWebDev: true, isPrimaryIT: true }
    ];

    this.skills = rawSkills.map(s => new Skill(s));

    // Initialize Experience Data
    const rawExperience: IExperience[] = [
      {
        id: 'exp-1',
        title: 'Bachelor of Science in Information Technology (BSIT)',
        organization: 'Tagoloan Community College',
        period: '2022 - 2026',
        type: 'education',
        description: 'Comprehensive degree in Information Technology focusing on Web Engineering, Software Architecture, Database Systems, and IT Infrastructure.',
        achievements: [
          'Capstone Project: Lumichat — An Expert Based Mental Health Support Chatbot for Students',
          'Capstone Role: Backend Developer (API Architecture, Database Design & Chatbot Logic)',
          'Published Capstone Research Paper in International Journal of Engineering Research & Technology (IJERT)'
        ],
        link: {
          text: 'View Published Research Paper (IJERT)',
          url: 'https://www.ijert.org/lumichat--an-expert-based-mental-health-support-chatbot-for-students'
        }
      },
      {
        id: 'exp-2',
        title: 'IT Technical Support & Administrative Data Entry Specialist',
        organization: 'Speakify Teams Corp. (3 Months Internship)',
        period: '3 Months Internship',
        type: 'experience',
        description: 'Provided IT Technical Support and Administrative Data Management to maintain office IT reliability and business records.',
        achievements: [
          'IT Technical Support: Managed hardware repair, workstation diagnostics, and network troubleshooting across company devices',
          'Administrative Data Entry: Executed high-accuracy data entry and digital record operations for subsidiary businesses',
          'Ensured hardware reliability and streamlined administrative workflow operations'
        ]
      }
    ];

    this.experiences = rawExperience.map(e => new Experience(e));
  }

  public getProjects(role: TargetRole = 'all', category: string = 'all'): Project[] {
    return this.projects.filter(p => p.matchesRole(role) && p.matchesCategory(category));
  }

  public getProjectById(id: string): Project | undefined {
    return this.projects.find(p => p.id === id);
  }

  public getSkills(role: TargetRole = 'all'): Skill[] {
    return this.skills.filter(s => s.isRelevantForRole(role));
  }

  public getExperiences(): Experience[] {
    return this.experiences;
  }
}
